import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { Close as CloseIcon, Restaurant as RecipeIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Logo } from 'images/logo.svg';
import RecipeCardTemplate from 'components/nutrition/RecipeCardTemplate';
import { DEFAULT_RECIPE_COLOR } from 'components/nutrition/recipeColors';

const watermarkStyles = {
  height: '100%',
  opacity: 0.04,
  position: 'absolute',
  right: 0,
  top: 0,
  transform: 'translateX(45%)',
  pointerEvents: 'none'
};

/**
 * Card compacta de receta (grilla). Click → dialog con la tarjeta de receta hermosa
 * (`RecipeCardTemplate`, misma que la preview del admin). La card lleva el color de acento.
 */
function RecipeCard({ recipe }) {
  const { t } = useTranslation(['nutrition']);
  const [open, setOpen] = useState(false);
  const accent = recipe.accent_color || DEFAULT_RECIPE_COLOR;

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') setOpen(true); }}
        sx={{
          position: 'relative', overflow: 'hidden', cursor: 'pointer',
          backgroundColor: 'surface.raised', border: '1px solid', borderColor: 'hairline.main',
          borderRadius: '16px', height: '100%', borderTop: `3px solid ${accent}`,
          boxShadow: (th) => th.palette.shadow.card,
          transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), background-color 0.2s',
          '&:hover': { transform: 'translateY(-2px)', backgroundColor: 'surface.hover' }
        }}
      >
        <Logo style={watermarkStyles} />
        {recipe.image_url ? (
          <Box component="img" src={recipe.image_url} alt={recipe.name} sx={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }} />
        ) : (
          <Box sx={{ width: '100%', height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'surface.overlay' }}>
            <RecipeIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
          </Box>
        )}
        <Box sx={{ p: 2, position: 'relative' }}>
          {recipe.meal_label && (
            <Typography sx={{ textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: 10, fontWeight: 700, color: accent, mb: 0.25 }}>
              {recipe.meal_label}
            </Typography>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>{recipe.name}</Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            {recipe.difficulty && <Chip size="small" label={recipe.difficulty} sx={{ backgroundColor: 'gold.soft', color: 'gold.main', fontWeight: 600 }} />}
            {recipe.portions != null && <Chip size="small" label={`${recipe.portions} ${t('portions', { ns: 'nutrition', defaultValue: 'porciones' })}`} variant="outlined" />}
          </Stack>
        </Box>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: { backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none', overflow: 'visible', width: { xs: '100%', md: 820 }, maxWidth: '96vw', m: { xs: 1, sm: 2 } } }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, zIndex: 2, backgroundColor: 'rgba(0,0,0,0.45)', color: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box sx={{ maxHeight: '92vh', overflowY: 'auto', borderRadius: '18px' }}>
            <RecipeCardTemplate recipe={recipe} />
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

RecipeCard.propTypes = { recipe: PropTypes.object.isRequired };

export default RecipeCard;
